import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clapperboard,
  Palette,
  Globe,
  Target,
  Sparkles,
  ChevronDown,
  ArrowRight,
} from "lucide-react";
import { LINKS } from "@/lib/links";

const services = [
  {
    icon: Clapperboard,
    tag: "Servizio core · Metodo RIM™",
    title: "Gestione Social Organica + Video Content",
    desc: "Il cuore di Rimfluences: gestiamo la tua presenza social con una strategia organica basata su video verticali, format ricorrenti, trend ed editing avanzato.",
    bullets: [
      "Strategia editoriale e format ricorrenti",
      "Riprese in loco e direzione creativa",
      "Editing avanzato, hook, sottotitoli e ritmo video",
      "Trend adattati al tuo brand",
      "Micro-collaborazioni locali e contenuti organici",
    ],
    featured: true,
  },
  {
    icon: Palette,
    tag: "Identità",
    title: "Branding & Grafiche",
    desc: "Diamo alla tua attività un’immagine più ordinata, professionale e riconoscibile.",
    bullets: [
      "Logo e identità visiva base",
      "Grafiche social e template",
      "Locandine e materiali promozionali",
    ],
  },
  {
    icon: Globe,
    tag: "Web & E-commerce",
    title: "Siti Web, Landing Page & Gestionali",
    desc: "Creiamo strumenti digitali semplici, moderni e utili per trasformare l’attenzione online in contatti, prenotazioni o richieste reali.",
    bullets: [
      "Siti vetrina e landing page",
      "Menu digitali, QR Code, collegamenti contatti",
      "Piccoli gestionali per attività locali",
      "TikTok Shop, Shopify",
    ],
  },
  {
    icon: Target,
    tag: "Advertising",
    title: "Advertising · Google Ads & Meta Ads",
    desc: "Gestiamo campagne su Meta e Google per aumentare visibilità, richieste, traffico e conversioni.",
    bullets: [
      "Campagne Meta Ads e Google Ads",
      "Setup tracking e conversioni",
      "Campagne per lead, traffico e vendite",
    ],
  },
];

function ServiceCard({ s, index }) {
  const [open, setOpen] = useState(index === 0);
  const Icon = s.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      data-testid={`service-card-${index}`}
      className={`relative rounded-2xl border ${
        s.featured ? "border-rim/55 bg-rim-surface" : "border-rim/20 bg-rim-surface"
      } overflow-hidden hover:border-rim/55 hover:scale-[1.01] transition-all duration-300`}
    >
      {s.featured && (
        <div className="absolute -top-px left-5 z-10">
          <span className="inline-flex items-center gap-1.5 bg-rim text-white text-[10px] font-semibold uppercase tracking-[0.16em] px-3 py-1.5 rounded-b-md shadow-lg">
            <Sparkles className="h-3 w-3" />
            Servizio core · Metodo RIM™
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`service-toggle-${index}`}
        className={`w-full flex items-start gap-4 p-5 md:p-6 ${
          s.featured ? "pt-9 md:pt-10" : ""
        } text-left`}
      >
        <div
          className={`h-11 w-11 rounded-xl grid place-items-center shrink-0 ${
            s.featured ? "bg-rim text-white" : "bg-rim/20 text-rim-glow"
          }`}
        >
          <Icon className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          {!s.featured && (
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-rim-muted mb-1">
              {s.tag}
            </p>
          )}
          <h3 className="font-heading text-lg md:text-xl font-bold text-white leading-tight">
            {s.title}
          </h3>
          <p className="mt-2 text-[14px] text-rim-muted leading-relaxed">
            {s.desc}
          </p>
        </div>
        <ChevronDown
          className={`h-5 w-5 text-rim-glow shrink-0 mt-2 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-5 md:px-6 pb-5 md:pb-6">
              <div className="border-t border-rim/15 pt-4">
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-rim-muted mb-3">
                  Cosa include
                </p>
                <ul className="space-y-2">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2.5 text-[14px] text-white/85 leading-snug"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-rim-glow shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={LINKS.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-rim-glow hover:text-white transition-colors"
                >
                  Scopri di più
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section
      id="servizi"
      data-testid="services-section"
      className="relative py-20 md:py-32 bg-rim-night text-rim-text"
    >
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_bottom,rgba(168,85,247,0.12),transparent_60%)]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-10 md:mb-14"
        >
          <h2
            data-testid="services-title"
            className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]"
          >
            Come possiamo aiutarti, concretamente.
          </h2>
          <p className="mt-5 font-heading text-lg md:text-2xl font-bold text-rim-glow leading-snug max-w-2xl">
            Tutto quello che serve per rendere la tua attività più
            riconoscibile online.
          </p>
        </motion.div>

        <div className="space-y-3 md:space-y-4 max-w-3xl mx-auto">
          {services.map((s, i) => (
            <ServiceCard key={s.title} s={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
